import React, { useState, useEffect, useRef } from 'react';

const STORE_KEY = 'seo_notes_v5';
function load(): State {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null') || { projects: [] }; }
  catch { return { projects: [] }; }
}
function persist(d: State) { localStorage.setItem(STORE_KEY, JSON.stringify(d)); }
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 5); }

interface Todo { id: string; text: string; done: boolean; subtasks?: Todo[]; }
interface Project { id: string; name: string; emoji: string; todos: Todo[]; pinned?: boolean; }
interface State { projects: Project[]; }

const EMOJIS = ['🚀','💼','🎯','⚡','🔥','💡','🌟','🛠️','📈','💎'];

const DEFAULTS: Project[] = [
  { id: uid(), name: 'Tkxel', emoji: '🚀', todos: [], pinned: true },
  { id: uid(), name: 'Reloadux', emoji: '💼', todos: [] },
];

export default function Notes() {
  const [state, setState] = useState<State>(() => {
    const s = load();
    return s.projects.length === 0 ? { projects: DEFAULTS } : s;
  });
  const [showDone, setShowDone] = useState(false);
  const [newName, setNewName] = useState('');
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [todoText, setTodoText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingSubtaskTo, setAddingSubtaskTo] = useState<{ pid: string; tid: string } | null>(null);
  const [subtaskText, setSubtaskText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const subtaskInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { persist(state); }, [state]);
  useEffect(() => { if (addingTo) setTimeout(() => inputRef.current?.focus(), 50); }, [addingTo]);
  useEffect(() => { if (addingSubtaskTo) setTimeout(() => subtaskInputRef.current?.focus(), 50); }, [addingSubtaskTo]);

  const update = (fn: (s: State) => State) => setState(fn);

  function addProject() {
    const name = newName.trim(); if (!name) return;
    const idx = state.projects.length;
    const emoji = EMOJIS[idx % EMOJIS.length];
    update(s => ({ projects: [...s.projects, { id: uid(), name, emoji, todos: [] }] }));
    setNewName('');
  }
  function deleteProject(id: string) {
    if (!confirm('Delete this project?')) return;
    update(s => ({ projects: s.projects.filter(p => p.id !== id) }));
  }
  function togglePin(id: string) {
    update(s => ({ projects: s.projects.map(p => p.id === id ? { ...p, pinned: !p.pinned } : p) }));
  }
  function renameProject(id: string, name: string) {
    if (name.trim()) update(s => ({ projects: s.projects.map(p => p.id === id ? { ...p, name: name.trim() } : p) }));
    setEditingId(null);
  }
  function addTodo(pid: string) {
    const text = todoText.trim();
    if (!text) { setAddingTo(null); return; }
    update(s => ({ projects: s.projects.map(p => p.id === pid ? { ...p, todos: [...p.todos, { id: uid(), text, done: false }] } : p) }));
    setTodoText('');
  }
  function toggleTodo(pid: string, tid: string) {
    update(s => ({ projects: s.projects.map(p => p.id === pid ? { ...p, todos: p.todos.map(t => t.id === tid ? { ...t, done: !t.done } : t) } : p) }));
  }
  function deleteTodo(pid: string, tid: string) {
    update(s => ({ projects: s.projects.map(p => p.id === pid ? { ...p, todos: p.todos.filter(t => t.id !== tid) } : p) }));
  }
  function addSubtask(pid: string, tid: string) {
    const text = subtaskText.trim();
    if (!text) { setAddingSubtaskTo(null); return; }
    update(s => ({
      projects: s.projects.map(p => p.id !== pid ? p : {
        ...p,
        todos: p.todos.map(t => t.id !== tid ? t : { ...t, subtasks: [...(t.subtasks || []), { id: uid(), text, done: false }] }),
      }),
    }));
    setSubtaskText('');
  }
  function toggleSubtask(pid: string, tid: string, sid: string) {
    update(s => ({
      projects: s.projects.map(p => p.id !== pid ? p : {
        ...p,
        todos: p.todos.map(t => t.id !== tid ? t : { ...t, subtasks: (t.subtasks || []).map(st => st.id === sid ? { ...st, done: !st.done } : st) }),
      }),
    }));
  }
  function deleteSubtask(pid: string, tid: string, sid: string) {
    update(s => ({
      projects: s.projects.map(p => p.id !== pid ? p : {
        ...p,
        todos: p.todos.map(t => t.id !== tid ? t : { ...t, subtasks: (t.subtasks || []).filter(st => st.id !== sid) }),
      }),
    }));
  }

  const totalPending = state.projects.reduce((a, p) => a + p.todos.filter(t => !t.done).length, 0);
  const totalDone   = state.projects.reduce((a, p) => a + p.todos.filter(t =>  t.done).length, 0);
  const sorted = [...state.projects].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  return (
    <div className="h-full flex flex-col gap-5">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">My Projects</h1>
          <div className="flex gap-1.5">
            {totalPending > 0 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-900 text-white">
                {totalPending} pending
              </span>
            )}
            {totalDone > 0 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                ✓ {totalDone} done
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {totalDone > 0 && (
            <button
              type="button"
              onClick={() => setShowDone(value => !value)}
              className="border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-sm font-bold shadow-sm transition-all"
            >
              {showDone ? 'Hide Done' : `Show Done (${totalDone})`}
            </button>
          )}
          <input type="text" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addProject()}
            placeholder="Project name..."
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 w-40 bg-white shadow-sm"
          />
          <button onClick={addProject}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition-all">
            + New
          </button>
        </div>
      </div>

      {/* ── Masonry ── */}
      <div className="flex-1 overflow-y-auto pb-4"
        style={{ columns: '2 320px', columnGap: '18px' }}>

        {sorted.map((project) => {
          const pending = project.todos.filter(t => !t.done).length;
          const done    = project.todos.filter(t =>  t.done).length;
          const total   = project.todos.length;
          const pct     = total > 0 ? Math.round((done / total) * 100) : 0;
          const isHere  = addingTo === project.id;
          const allDone = total > 0 && done === total;
          const sortedTodos = [...project.todos].sort((a, b) => Number(a.done) - Number(b.done));
          const visibleTodos = showDone ? sortedTodos : sortedTodos.filter(todo => !todo.done);

          return (
            <div key={project.id} className="group"
              style={{ breakInside: 'avoid', marginBottom: '18px', display: 'inline-block', width: '100%' }}>

              <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-shadow duration-300">

                {/* ── Header ── */}
                <div className="relative px-5 pt-5 pb-4 bg-[#18181B]">

                  {/* Hover actions */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => togglePin(project.id)}
                      title={project.pinned ? 'Unpin' : 'Pin'}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs bg-white/10 hover:bg-white/20 transition-colors text-white">
                      {project.pinned ? '📌' : '📍'}
                    </button>
                    <button onClick={() => deleteProject(project.id)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs bg-white/10 hover:bg-red-500/60 transition-colors text-white font-bold">
                      ✕
                    </button>
                  </div>

                  {/* Top badges row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {project.pinned && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400">📌 Pinned</span>
                      )}
                    </div>
                    {/* Pending alert badge */}
                    {pending > 0 ? (
                      <div className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg"
                        style={{ boxShadow: '0 0 10px rgba(249,115,22,0.5)' }}>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                        </svg>
                        {pending} pending
                      </div>
                    ) : total > 0 ? (
                      <div className="flex items-center gap-1.5 bg-white/10 text-white/60 text-xs font-bold px-2.5 py-1 rounded-full">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                        All done!
                      </div>
                    ) : null}
                  </div>

                  {/* Title */}
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 bg-white/10">
                      {project.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingId === project.id ? (
                        <input autoFocus defaultValue={project.name}
                          onBlur={e => renameProject(project.id, e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') renameProject(project.id, (e.target as HTMLInputElement).value);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="font-bold text-lg text-white w-full bg-transparent border-b border-white/40 outline-none"
                        />
                      ) : (
                        <h3 className="font-bold text-white text-lg leading-tight truncate cursor-pointer"
                          onDoubleClick={() => setEditingId(project.id)}>
                          {project.name}
                        </h3>
                      )}
                      <p className="text-xs mt-0.5 text-white/40">
                        {total === 0 ? 'No tasks yet' : allDone ? '🎉 All tasks done!' : `${done} of ${total} done`}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {total > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-white/40 font-semibold uppercase tracking-widest">Progress</span>
                        <span className="text-xs font-bold text-orange-400">{pct}%</span>
                      </div>
                      {/* Track */}
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700 bg-orange-500"
                          style={{ width: pct + '%' }} />
                      </div>
                      {/* Tick marks */}
                      <div className="flex justify-between mt-1 px-px">
                        {[0,25,50,75,100].map(v => (
                          <div key={v} className={`w-px h-1 rounded-full ${pct >= v ? 'bg-orange-400' : 'bg-white/15'}`} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Body ── */}
                <div className="bg-white">

                  {/* Task list */}
                  {visibleTodos.length > 0 && (
                    <div className="divide-y divide-gray-50">
                      {visibleTodos.map((todo, idx) => {
                        const subtasks = todo.subtasks || [];
                        const subDone = subtasks.filter(st => st.done).length;
                        const visibleSubtasks = showDone ? subtasks : subtasks.filter(st => !st.done);
                        const isAddingSubtask = addingSubtaskTo?.pid === project.id && addingSubtaskTo?.tid === todo.id;

                        return (
                          <div key={todo.id}>
                            <div className="flex items-start gap-3 px-5 py-3 hover:bg-gray-100 transition-colors group/t cursor-pointer"
                              onClick={() => toggleTodo(project.id, todo.id)}>

                              {/* Checkbox — square shape, not just color */}
                              <div className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-150 mt-0.5 ${
                                todo.done
                                  ? 'bg-gray-900 border-gray-900'
                                  : 'border-gray-300 bg-white'
                              }`}>
                                {todo.done && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                                  </svg>
                                )}
                              </div>

                              {/* Task number + text */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-1.5">
                                  <span className="text-[10px] text-gray-300 font-mono mt-0.5 flex-shrink-0">
                                    {String(idx + 1).padStart(2, '0')}
                                  </span>
                                  <span className={`text-sm leading-snug transition-all ${
                                    todo.done ? 'line-through text-gray-300' : 'text-gray-700'
                                  }`}>
                                    {todo.text}
                                  </span>
                                  {subtasks.length > 0 && (
                                    <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">
                                      {subDone}/{subtasks.length}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <button onClick={e => { e.stopPropagation(); setAddingSubtaskTo({ pid: project.id, tid: todo.id }); setSubtaskText(''); }}
                                title="Add subtask"
                                className="opacity-0 group-hover/t:opacity-100 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-gray-500 hover:text-white hover:bg-gray-900 transition-all flex-shrink-0 mt-0.5 border border-gray-200 hover:border-gray-900">
                                +
                              </button>
                              <button onClick={e => { e.stopPropagation(); deleteTodo(project.id, todo.id); }}
                                className="opacity-0 group-hover/t:opacity-100 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-gray-500 hover:text-white hover:bg-red-500 transition-all flex-shrink-0 mt-0.5 border border-gray-200 hover:border-red-500">
                                ✕
                              </button>
                            </div>

                            {/* Subtasks — indented under the parent task */}
                            {visibleSubtasks.length > 0 && (
                              <div className="pl-8 pr-5 bg-gray-50/60">
                                {visibleSubtasks.map(st => (
                                  <div key={st.id}
                                    className="flex items-start gap-2.5 py-2 border-t border-gray-100 group/st cursor-pointer"
                                    onClick={e => { e.stopPropagation(); toggleSubtask(project.id, todo.id, st.id); }}>
                                    <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-150 mt-0.5 ${
                                      st.done ? 'bg-gray-400 border-gray-400' : 'border-gray-300 bg-white'
                                    }`}>
                                      {st.done && (
                                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                                        </svg>
                                      )}
                                    </div>
                                    <span className={`text-xs leading-snug flex-1 transition-all ${
                                      st.done ? 'line-through text-gray-300' : 'text-gray-600'
                                    }`}>
                                      {st.text}
                                    </span>
                                    <button onClick={e => { e.stopPropagation(); deleteSubtask(project.id, todo.id, st.id); }}
                                      className="opacity-0 group-hover/st:opacity-100 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-gray-400 hover:text-white hover:bg-red-500 transition-all flex-shrink-0 border border-gray-200 hover:border-red-500">
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Add subtask input */}
                            {isAddingSubtask && (
                              <div className="pl-8 pr-5 py-2 bg-gray-50/60 border-t border-gray-100">
                                <div className="flex gap-2 items-center">
                                  <div className="flex-shrink-0 w-4 h-4 rounded border-2 border-dashed border-gray-300" />
                                  <input ref={subtaskInputRef} type="text" value={subtaskText}
                                    onChange={e => setSubtaskText(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') addSubtask(project.id, todo.id);
                                      if (e.key === 'Escape') { setAddingSubtaskTo(null); setSubtaskText(''); }
                                    }}
                                    onBlur={() => setTimeout(() => { if (!subtaskText.trim()) setAddingSubtaskTo(null); }, 150)}
                                    placeholder="Subtask and press Enter..."
                                    className="flex-1 text-xs outline-none text-gray-700 placeholder-gray-400 bg-transparent"
                                  />
                                  <button onMouseDown={() => addSubtask(project.id, todo.id)}
                                    className="text-[10px] font-bold px-2 py-1 rounded text-white bg-gray-900 hover:bg-gray-700 transition-all">
                                    Add
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add task */}
                  {isHere ? (
                    <div className="px-4 py-3 border-t border-gray-100">
                      <div className="flex gap-2 items-center">
                        <div className="flex-shrink-0 w-5 h-5 rounded-md border-2 border-dashed border-gray-300" />
                        <input ref={inputRef} type="text" value={todoText}
                          onChange={e => setTodoText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') addTodo(project.id);
                            if (e.key === 'Escape') { setAddingTo(null); setTodoText(''); }
                          }}
                          onBlur={() => setTimeout(() => { if (!todoText.trim()) setAddingTo(null); }, 150)}
                          placeholder="Type task and press Enter..."
                          className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent"
                        />
                        <button onMouseDown={() => addTodo(project.id)}
                          className="text-xs font-bold px-3 py-1 rounded-lg text-white bg-gray-900 hover:bg-gray-700 transition-all">
                          Add
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setAddingTo(project.id); setTodoText(''); }}
                      className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all border-t border-gray-100 group/add">
                      <div className="w-5 h-5 rounded-md border-2 border-dashed border-gray-200 group-hover/add:border-gray-400 flex items-center justify-center transition-colors flex-shrink-0 text-[11px]">
                        +
                      </div>
                      <span className="text-sm">Add a task...</span>
                    </button>
                  )}

                  {/* Footer */}
                  {total > 0 && (
                    <div className="px-5 py-2.5 flex items-center justify-between border-t border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-2">
                        {/* Shape-based indicators: filled squares = done, empty = pending */}
                        <div className="flex gap-0.5 items-center">
                          {Array.from({ length: Math.min(total, 10) }).map((_, i) => (
                            <div key={i} className={`w-2 h-2 rounded-sm transition-all ${
                              i < done ? 'bg-gray-900' : 'bg-gray-200'
                            }`} />
                          ))}
                          {total > 10 && <span className="text-[9px] text-gray-400 ml-1">+{total - 10}</span>}
                        </div>
                      </div>
                      {allDone ? (
                        <span className="text-[11px] font-bold text-gray-900">🎉 Complete!</span>
                      ) : (
                        <span className="text-[11px] text-gray-400 font-medium">{pending} remaining</span>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          );
        })}

        {state.projects.length === 0 && (
          <div className="text-center py-20 text-gray-400">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-sm">Add your first project above</p>
          </div>
        )}
      </div>
    </div>
  );
}
